import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";

interface ButtonOptionInputProps {
  option: { label: string; value: string } | string;
  optionIndex: number;
  onSave: (optionIndex: number, value: string) => void;
  onDelete: (optionIndex: number) => void;
}

export const ButtonOptionInput = ({
  option,
  optionIndex,
  onSave,
  onDelete
}: ButtonOptionInputProps) => {
  // Extract the initial value from the option
  const getInitialValue = () => {
    if (typeof option === 'object' && option !== null && 'label' in option) {
      return option.label || '';
    } else if (typeof option === 'string') {
      return option;
    }
    return '';
  };

  // Local state for the option value
  const [value, setValue] = useState(getInitialValue());
  
  // Track if the input is being edited
  const isEditingRef = useRef(false);
  
  // Track if this is the initial render
  const initialRenderRef = useRef(true);

  // Update local state when props change, but only if we're not editing
  useEffect(() => {
    if (!isEditingRef.current && !initialRenderRef.current) {
      setValue(getInitialValue());
    }
    initialRenderRef.current = false;
  }, [option]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    isEditingRef.current = true;
    setValue(e.target.value);
  };

  const handleBlur = () => {
    if (isEditingRef.current) {
      onSave(optionIndex, value);
      isEditingRef.current = false;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  };

  return (
    <div className="flex items-center gap-2">
      <input
        type="text"
        value={value}
        onChange={handleChange}
        onFocus={() => { isEditingRef.current = true; }}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={`flex-1 p-2 border rounded-md text-sm ${!value ? 'text-gray-400' : ''}`}
        placeholder={`Button ${optionIndex + 1} Text`}
      />
      <Button
        variant="ghost"
        size="icon"
        onClick={() => onDelete(optionIndex)}
      >
        <Trash2 className="h-4 w-4 text-red-500" />
      </Button>
    </div>
  );
};
